import React, { useContext } from 'react';
import { Link } from 'react-router-dom';
import ThemeContext from '../../context/ThemeContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from 'react-i18next';

const LegalLayout = ({ children, title, subtitle }) => {
  const { theme, toggleTheme } = useContext(ThemeContext) || {};
  const { t } = useTranslation('legal');
  const isDark = theme === 'dark';
  const logoSrc = '/logoMuzo.svg';
  const logoColor = isDark ? '#ffffff' : '#6366f1';

  return (
    <div className={`min-h-screen transition-colors duration-300 ${
      isDark ? 'bg-slate-950 text-slate-100' : 'bg-slate-50 text-slate-900'
    }`}>
      {/* Header */}
      <header className={`sticky top-0 z-50 backdrop-blur-md border-b ${
        isDark ? 'bg-slate-950/80 border-slate-800' : 'bg-white/80 border-slate-200'
      }`}>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 group">
            <div className="relative w-8 h-8">
              <div 
                className="w-full h-full transition-transform group-hover:scale-110"
                style={{
                  maskImage: `url(${logoSrc})`,
                  WebkitMaskImage: `url(${logoSrc})`,
                  maskRepeat: 'no-repeat',
                  WebkitMaskRepeat: 'no-repeat',
                  maskSize: 'contain',
                  WebkitMaskSize: 'contain',
                  maskPosition: 'center',
                  WebkitMaskPosition: 'center',
                  backgroundColor: logoColor,
                }}
                aria-label="Muzo Studio"
              />
            </div>
            <span className="font-bold text-xl tracking-tight">
              Muzo<span className="text-indigo-500">Studio</span>
            </span>
          </Link>

          <div className="flex items-center gap-4">
            <button
              onClick={toggleTheme}
              className={`p-2 rounded-full transition-colors ${
                isDark ? 'hover:bg-slate-800 text-slate-400' : 'hover:bg-slate-100 text-slate-600'
              }`}
              aria-label={isDark ? t('actions.lightMode', { ns: 'common' }) : t('actions.darkMode', { ns: 'common' })}
            >
              <FontAwesomeIcon icon={isDark ? 'sun' : 'moon'} />
            </button>
            <Link 
              to="/login"
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                isDark 
                  ? 'bg-slate-800 hover:bg-slate-700 text-white' 
                  : 'bg-white hover:bg-slate-50 text-slate-900 border border-slate-200 shadow-sm'
              }`}
            >
              {t('layout.login')}
            </Link>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
        {(title || subtitle) && (
          <div className="text-center mb-12">
            {title && (
              <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight mb-4">
                {title}
              </h1>
            )}
            {subtitle && (
              <p className={`text-lg max-w-2xl mx-auto ${
                isDark ? 'text-slate-400' : 'text-slate-600'
              }`}>
                {subtitle}
              </p>
            )}
          </div>
        )}
        
        <div className={`rounded-2xl p-6 sm:p-10 shadow-xl ${
          isDark ? 'bg-slate-900/50 border border-slate-800' : 'bg-white border border-slate-100'
        }`}>
          {children}
        </div>
      </main>

      {/* Footer */}
      <footer className={`border-t py-12 ${
        isDark ? 'border-slate-800 bg-slate-950' : 'border-slate-200 bg-slate-50'
      }`}>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col md:flex-row justify-between items-center gap-6">
          <div className="text-sm text-slate-500">
            © {new Date().getFullYear()} Muzo Studio. {t('layout.footer')}
          </div>
          <div className="flex gap-6 text-sm font-medium">
            <Link to="/legal" className="text-slate-500 hover:text-indigo-500 transition-colors">
              {t('layout.mentions')}
            </Link>
            <Link to="/legal/cgu" className="text-slate-500 hover:text-indigo-500 transition-colors">
              {t('layout.cgu')}
            </Link>
            <a href="mailto:contact@muzo-studio.app" className="text-slate-500 hover:text-indigo-500 transition-colors">
              {t('layout.contact')}
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LegalLayout;
